import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Info, PauseCircle, Printer, RefreshCw, Send, SkipForward } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { BulkResult } from '../ui/BulkResult';
import { CopyableText } from '../ui/CopyableText';
import { ErrorState } from '../ui/ErrorState';
import { FilterableSelect } from '../ui/FilterableSelect';
import { ProgressBar } from '../ui/ProgressBar';
import {
  changeDeliveriesCourier,
  courierOptionsKey,
  fetchCourierOptions,
} from '../../features/deliveries/deliveriesThunks';
import { formatCourier } from '../../features/deliveries/deliveriesUtils';
import { selectProgress } from '../../features/feedback/feedbackSlice';

const WHAT_HAPPENS = [
  {
    icon: RefreshCw,
    text: 'Cambia el courier de los envíos por asignar, pendientes, creados en el courier o preparados. Los demás se omiten, y te decimos por qué.',
  },
  {
    icon: PauseCircle,
    text: 'Si el seller tiene stand-by con el courier nuevo, el envío vuelve a «Por asignar», sin tracking ni etiqueta, para detallar sus bultos.',
  },
  {
    icon: Send,
    text: 'Si no, se envía de inmediato al courier nuevo, y el envío anterior se anula solo en los couriers que funcionan sobre SimpliRoute. En los demás casos te listamos el tracking anterior para que lo anules a mano.',
  },
  {
    icon: Printer,
    text: 'Las etiquetas cambian: reimprímelas cuando veas el tracking nuevo en el historial.',
  },
  {
    icon: SkipForward,
    text: 'Se omiten los cedidos, los que despacha Mercado Libre y los que ya están con ese courier.',
  },
];

const ROW_LIST = 'max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-xl';

/** The order behind a row, or the shipment itself when the backend found none. */
const shipmentOf = (row) => (row.order_id ? `#${row.order_id}` : `Envío ${row.delivery_id}`);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const courierPlaceholder = (options) => {
  if (!options) return 'Cargando couriers...';
  return options.length > 0 ? 'Elige el courier' : 'Sin couriers habilitados';
};

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-xs font-bold uppercase tracking-wider text-text-muted">{title}</h3>
    {children}
  </div>
);

const Note = ({ children }) => (
  <div className="flex items-start gap-2.5 bg-ninja-gray dark:bg-[#111] border border-border rounded-xl px-4 py-3">
    <Info size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
    <p className="text-xs text-text-secondary">{children}</p>
  </div>
);

const ChangeResult = ({ result, onClose }) => {
  const toCancel = result.queued.filter((row) => row.previous_cancellation === 'manual');
  const autoCancelled = result.queued.filter((row) => row.previous_cancellation === 'auto').length;
  const onStandBy = result.queued.filter((row) => row.mode === 'stand_by').length;
  const failureReasons = [...new Set(result.failed.map((row) => row.message))];

  return (
    <BulkResult
      result={{
        requested: result.requested,
        succeeded: result.queued,
        skipped: result.skipped,
        failed: result.failed,
      }}
      succeededLabel="En cola"
      onClose={onClose}
    >
      {/* Its own scroll: with long lists the dialog outgrows a laptop screen,
          and the shared Modal does not scroll. */}
      <div className="flex flex-col gap-4 max-h-[45vh] overflow-y-auto pr-1">
        {failureReasons.length > 0 && (
          <ErrorState
            variant="inline"
            title={`${result.failed.length} sin cambiar`}
            description={`${failureReasons.join(' ')} Quedan seleccionados para que puedas reintentar.`}
          />
        )}

        {onStandBy > 0 && (
          <Note>
            {plural(onStandBy, 'envío vuelve', 'envíos vuelven')} a «Por asignar», sin tracking ni
            etiqueta, porque su seller tiene stand-by con {formatCourier(result.courier)}: detalla
            sus bultos para que salgan.
          </Note>
        )}

        {result.skipped.length > 0 && (
          <Section title="Omitidos">
            <ul className={ROW_LIST}>
              {result.skipped.map((row) => (
                <li
                  key={row.delivery_id}
                  data-testid="change-courier-skipped"
                  data-reason={row.reason}
                  className="flex flex-col px-3 py-2"
                >
                  <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                    {shipmentOf(row)}
                    {row.reference_id && (
                      <span className="font-normal text-text-secondary"> · {row.reference_id}</span>
                    )}
                  </span>
                  <span className="text-xs text-text-secondary">{row.message}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {toCancel.length > 0 && (
          <Section title="Anular a mano en el courier anterior">
            <p className="text-xs text-text-secondary">
              Su courier anterior no deja anular el envío desde aquí. Anúlalo en su sistema cuando
              veas el cambio en el historial (puede tardar unos minutos). Si un envío sigue con su
              tracking anterior, no lo anules: sigue siendo el válido.
            </p>
            <ul className={ROW_LIST}>
              {toCancel.map((row) => (
                <li
                  key={row.delivery_id}
                  data-testid="change-courier-manual-cancel"
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                      {shipmentOf(row)}
                    </span>
                    <span className="text-xs text-text-secondary">{formatCourier(row.previous_courier)}</span>
                  </div>
                  <CopyableText
                    value={row.previous_tracking}
                    maxWidth="140px"
                    className="text-xs font-medium text-ninja-dark dark:text-white"
                  />
                </li>
              ))}
            </ul>
          </Section>
        )}

        {autoCancelled > 0 && (
          <Note>
            En {plural(autoCancelled, 'envío', 'envíos')} el envío anterior se anula solo, porque su
            courier funciona sobre SimpliRoute. Si ya estaba en una ruta, ese envío no cambia y queda
            como estaba.
          </Note>
        )}
      </div>
    </BulkResult>
  );
};

/**
 * Hands the selected shipments to another courier, with the rules of the
 * order detail's «Cambiar courier».
 *
 * Shared by the three workspaces. The couriers it offers are the operator's:
 * the session's own, or, for the staff, those of the operator it filtered by
 * (`adminId`). It ends on a tally instead of closing, because three outcomes
 * need the operator: the shipments skipped, the ones back on «Por asignar»
 * under their seller's stand-by, and the old shipments to cancel by hand.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.deliveryIds
 * @param {number|string|null} [props.adminId] - The operator whose couriers to offer; the session's when absent.
 * @param {(failedIds: number[]) => void} [props.onFinished] - Called when the
 *   result is closed, with the delivery ids that did not change, so the page
 *   can keep them selected for a retry.
 */
export const ChangeCourierBulkModal = ({ isOpen, onClose, deliveryIds = [], adminId = null, onFinished }) => {
  const dispatch = useDispatch();
  const key = courierOptionsKey(adminId);
  const options = useSelector((state) => state.deliveries.courierOptions[key]);
  const optionsError = useSelector((state) => state.deliveries.courierOptionsError[key]);

  const [courier, setCourier] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // A selection over 100 goes out in batches; the thunk reports which one.
  const [requestId, setRequestId] = useState(null);
  const progress = useSelector(selectProgress(requestId));

  useEffect(() => {
    if (isOpen) dispatch(fetchCourierOptions(adminId));
  }, [dispatch, isOpen, adminId]);

  const handleClose = () => {
    if (sending) return;
    const finished = result;
    setCourier('');
    setError(null);
    setResult(null);
    onClose();
    if (finished) onFinished?.(finished.failed.map((row) => row.delivery_id));
  };

  const handleConfirm = async () => {
    setSending(true);
    setError(null);

    const running = dispatch(changeDeliveriesCourier({ deliveryIds, courier }));
    setRequestId(running.requestId);

    try {
      setResult(await running.unwrap());
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudo cambiar el courier de los envíos');
    } finally {
      setSending(false);
    }
  };

  const count = deliveryIds.length;
  const courierOptions = (options || []).map((item) => ({ value: item.courier, label: formatCourier(item.courier) }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cambiar courier"
      testId="change-courier-modal"
      busy={sending}
    >
      {result ? (
        <ChangeResult result={result} onClose={handleClose} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Vas a pasar {plural(count, 'envío', 'envíos')} a otro courier.
          </p>

          {optionsError ? (
            <ErrorState
              variant="inline"
              title="No se pudieron cargar los couriers"
              description={optionsError}
              onRetry={() => dispatch(fetchCourierOptions(adminId))}
            />
          ) : (
            <FilterableSelect
              label="Courier nuevo"
              variant="form"
              value={courier}
              onChange={setCourier}
              options={courierOptions}
              placeholder={courierPlaceholder(options)}
              disabled={sending || courierOptions.length === 0}
              testId="change-courier-select"
            />
          )}

          <ul className="flex flex-col gap-3">
            {WHAT_HAPPENS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-2.5 text-sm text-text-secondary">
                <Icon size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          {error && <ErrorState variant="inline" title="No se pudo cambiar" description={error} />}

          {sending && progress && (
            <ProgressBar
              value={progress.done}
              max={progress.total}
              label={progress.label}
              testId="bulk-progress"
            />
          )}

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={sending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleConfirm}
              loading={sending}
              loadingLabel="Cambiando..."
              disabled={count === 0 || !courier}
              data-testid="change-courier-confirm"
            >
              Confirmar cambio
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
